import { useCallback, useRef } from "react";
import { useMapContext, useMapViewActions } from "@evergis/react";
import { QueryLayerServiceConfigurationDc } from "@evergis/api";
import { Bbox } from "@evergis/sgis";

import { api } from "../../../evergis/api";
import { DEFAULT_DATA_SOURCE_LIMIT, FILTER_PREFIX } from "../../FeatureCard/constants";
import { applyVarsToCondition, formatDataSourceCondition } from "../utils";
import { useProjectSrid } from "../../../ducks/project/hooks";
import { useEwktGeometry } from "../../FeatureSelector/hooks/useEwktGeometry";
import { FeatureAttribute } from "../../FeatureCard/components/Attributes/FeatureAttributeField";
import { FeatureDataSource } from "../../../ducks/showFeature/types";
import {
  ConfigContainerChild,
  ConfigDataSource,
  ConfigFilter,
  DataSourcePromise,
  EqlDataSource,
  SelectedFilters,
} from "../../FeatureCard/types";

export const useDataSources = ({
  config,
  filters,
  attributes,
  layerParams,
  eqlParameters,
}: {
  config: ConfigContainerChild;
  filters: SelectedFilters;
  attributes?: FeatureAttribute[];
  layerParams?: Record<string, string>;
  eqlParameters?: QueryLayerServiceConfigurationDc["eqlParameters"];
}) => {
  const { map, painter } = useMapContext();
  const sr = useProjectSrid();
  const { animateTo } = useMapViewActions();
  const ewktGeometry = useEwktGeometry();

  const dataSources: ConfigDataSource[] = config?.dataSources || [];
  const configFilters: ConfigFilter[] = config?.filters || [];

  const prevFilters = useRef<SelectedFilters>({});

  const getDataSourcePromises = useCallback(
    async ({ layerName, condition, limit, query, url, resourceId, type, parameters }: ConfigDataSource) => {
      const formattedParameters =
        !url && !resourceId && !query
          ? null
          : Object.keys(parameters).reduce(
              (result, param) => ({
                ...result,
                [param]: applyVarsToCondition({
                  section: parameters[param],
                  configFilters,
                  filters,
                  attributes,
                  eqlParameters,
                  layerParams,
                  geometry: ewktGeometry,
                }),
              }),
              {},
            );

      if (url || resourceId) {
        const response = await fetch(
          url || `https://beta5.evergis.ru/sp/${type}/resource/run?resourceId=${resourceId}`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formattedParameters),
          },
        );
        const json = await response.json();

        return {
          items: json.result.items,
          attributeDefinition: json.result.attributeDefinition,
        } as EqlDataSource;
      }

      if (query) {
        const queryResponse = await api.eql.getQueryResult({ query, limit, parameters: formattedParameters });
        const descriptionResponse = await api.eql.getQueryDescription({
          query,
          limit,
          parameters: formattedParameters,
        });

        return {
          // hot fix, need to update spcore_api:
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          items: (queryResponse as any).items,
          attributeDefinition: descriptionResponse,
        } as EqlDataSource;
      }

      return api.layers.getFeatures(layerName, {
        offset: 0,
        limit: limit || DEFAULT_DATA_SOURCE_LIMIT,
        withGeom: false,
        query: formatDataSourceCondition({
          condition,
          configFilters,
          filters,
          attributes,
          layerParams,
          eqlParameters,
          geometry: ewktGeometry,
        }),
      });
    },
    [attributes, configFilters, eqlParameters, ewktGeometry, filters, layerParams],
  );

  const getUpdatingDataSources = useCallback(() => {
    const diffFilterNames = Object.keys(filters).filter(key =>
      Array.isArray(filters[key].value)
        ? (filters[key].value as string[]).length !== (prevFilters.current[key]?.value as string[])?.length ||
          (filters[key].value as string[]).some((item, index) => item !== prevFilters.current[key]?.value?.[index])
        : filters[key].value !== prevFilters.current[key]?.value,
    );

    if (!diffFilterNames.length) return;

    const filteredDataSources = dataSources?.filter(({ condition }) =>
      diffFilterNames.some(diffFilterName => condition?.includes(`${FILTER_PREFIX}${diffFilterName}`)),
    );

    if (!filteredDataSources?.length) return;

    prevFilters.current = JSON.parse(JSON.stringify(filters));

    return filteredDataSources;
  }, [dataSources, filters]);

  const getUpdatedDataSources = useCallback(
    (responses: DataSourcePromise[], currentDataSources: ConfigDataSource[], otherDataSources: FeatureDataSource[]) => {
      const newDataSources = JSON.parse(JSON.stringify([...currentDataSources, ...otherDataSources]));

      responses.forEach((response, index) => {
        const isQueryDataSource =
          !!currentDataSources[index].query ||
          !!currentDataSources[index].url ||
          !!currentDataSources[index].resourceId;

        newDataSources[index].layerName = currentDataSources[index].layerName;

        newDataSources[index].features =
          response.status === "rejected"
            ? null
            : isQueryDataSource
            ? response.value.items?.map((item, itemIndex) => ({ id: itemIndex + 1, attributes: item })) || []
            : response.value.items;

        newDataSources[index].attributeDefinition =
          response.status === "rejected" || !isQueryDataSource
            ? null
            : (response.value as EqlDataSource).attributeDefinition;
      });

      return newDataSources;
    },
    [],
  );

  const zoomToLayersExtent = useCallback(
    async (layers: Array<{ layerName: string; query: string }>) => {
      const response = await fetch(`${api.url}bulk/layers/extent?sr=${sr}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(layers),
      });
      const newExtent = await response.json();
      const { coordinates } = newExtent.overall || {};

      if (!coordinates) return;

      const hasEmptyCoordinate = coordinates.some(([x, y]) => !x && !y);

      if (hasEmptyCoordinate) return;

      const bbox = new Bbox(coordinates[0], coordinates[1], map.crs);
      const resolution = Math.max(bbox.width / painter.width, bbox.height / painter.height);

      animateTo({
        position: bbox.center,
        resolution: map.getAdjustedResolution(resolution),
      });
    },
    [animateTo, map, painter.height, painter.width, sr],
  );

  return {
    getDataSourcePromises,
    getUpdatingDataSources,
    getUpdatedDataSources,
    zoomToLayersExtent,
  };
};
